import { Environment, AppConfig, ApiConfig, TrackingConfig, Auth0Config, PaymentConfig } from './types';
import { config } from '@dotenvx/dotenvx';

// Try to load environment variables, but don't fail if it doesn't work
try {
  config();
  console.log('dotenvx config() executed successfully');
} catch (error) {
  console.warn('dotenvx config() failed:', error);
  console.log('Continuing with process.env variables');
}

// Default environment
const DEFAULT_ENVIRONMENT: Environment = 'production';

/**
 * Get the current environment
 */
export function getEnvironment(): Environment {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return process.env.NODE_ENV as Environment || DEFAULT_ENVIRONMENT;
  }

  // Check local storage for saved preference
  const storedEnv = localStorage.getItem('app_environment');
  if (storedEnv && isValidEnvironment(storedEnv)) {
    return storedEnv as Environment;
  }
  
  // Default to current NODE_ENV or development
  return process.env.NODE_ENV as Environment || DEFAULT_ENVIRONMENT;
}

/**
 * Validate that a string is a valid environment
 */
export function isValidEnvironment(env: string): env is Environment {
  return ['development', 'staging', 'production', 'local'].includes(env);
}

/**
 * Get API configuration from environment variables
 */
export function getApiConfig(): ApiConfig {
  return {
    baseURL: process.env.REACT_APP_API_BASE_URL || '',
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10),
    headers: {
      'Content-Type': process.env.REACT_APP_API_CONTENT_TYPE || 'application/json'
    }
    // PinPayments configuration removed as part of transition to Stripe
  };
}

/**
 * Get tracking configuration from environment variables
 */
export function getTrackingConfig(): TrackingConfig | undefined {
  const terminalStatusesStr = process.env.REACT_APP_TRACKING_TERMINAL_STATUSES;
  if (!terminalStatusesStr) return undefined;
  
  return {
    terminalStatuses: terminalStatusesStr.split(',')
  };
}

/**
 * Get default tracking window hours (fallback for backward compatibility)
 * @deprecated Use transaction.remainingHoursForTracking from the API instead
 */
export function getDefaultTrackingWindowHours(): number {
  return 96; // Default to 96 hours (4 days)
}

/**
 * Get Auth0 configuration from environment variables
 */
export function getAuth0Config(): Auth0Config | undefined {
  if (!process.env.REACT_APP_AUTH0_DOMAIN || !process.env.REACT_APP_AUTH0_CLIENT_ID) {
    return undefined;
  }
  
  return {
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
    clientSecret: process.env.REACT_APP_AUTH0_CLIENT_SECRET,
    redirect_uri: process.env.REACT_APP_AUTH0_REDIRECT_URI || '',
    callbackUrl: process.env.REACT_APP_AUTH0_CALLBACK_URL || '',
    logoutUrl: process.env.REACT_APP_AUTH0_LOGOUT_URL || '',
    audience: process.env.REACT_APP_AUTH0_AUDIENCE || ''
  };
}

/**
 * Get payment configuration from environment variables
 */
export function getPaymentConfig(): PaymentConfig {
  return {
    platformFee: {
      percentage: parseFloat(process.env.REACT_APP_PLATFORM_FEE_PERCENTAGE || '0.00'),
      minimumFee: parseFloat(process.env.REACT_APP_PLATFORM_FEE_MINIMUM || '0.00')
    },
    stripe: {
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
    }
  };
}

/**
 * Get the complete configuration
 */
export function getConfig(): AppConfig {
  return {
    api: getApiConfig(),
    tracking: getTrackingConfig(),
    auth0: getAuth0Config(),
    payment: getPaymentConfig()
  };
}

/**
 * Switch to a different environment
 * Note: This requires a page reload to take effect with dotenvx
 */
export function setEnvironment(env: Environment): void {
  // Validate environment
  if (!isValidEnvironment(env)) {
    console.error(`Invalid environment: ${env}`);
    return;
  }
  
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    console.warn('Cannot persist environment outside of browser environment');
    return;
  }
  
  // Store in local storage for persistence
  localStorage.setItem('app_environment', env);
  
  // Log the change
  console.log(`[Config] Environment switched to ${env}`);
  
  // Reload the page to apply the new environment
  window.location.reload();
}

// Create a custom event for configuration changes
export const configChangeEvent = new EventTarget();

// Notify about configuration changes
export function notifyConfigChange(): void {
  configChangeEvent.dispatchEvent(new CustomEvent('configChanged', {
    detail: { 
      environment: getEnvironment(), 
      config: getConfig() 
    }
  }));
}

// Re-export types for use in other components
export type { Environment, AppConfig, ApiConfig, PaymentConfig };
export type { TrackingConfig } from './types';

// Re-export Stripe Connect theme
export { stripeConnectTheme } from './stripeTheme';